import { FC } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { Button, PhoneField, CheckboxField, useFormField, Text } from '@frontend/design-system';
import { AIRSalesDemoTrackingIds } from '../../tracking-ids';

interface PhoneNumberSetupProps {
  onStart: (phoneNumber: string, clearMessageHistory: boolean) => void;
  onClose?: () => void;
}

interface PhoneNumberConfirmationProps {
  phoneNumber: string;
}

export const PhoneNumberConfirmation: FC<PhoneNumberConfirmationProps> = ({ phoneNumber }) => {
  const { t } = useTranslation('ai-receptionist');

  return (
    <div css={[styles.container, styles.confirmationContainer]}>
      <Icon name='mark-done' size={24} css={styles.icon} />
      <Text size='large'>
        {t('The AI Receptionist will send appointment confirmations to {{phoneNumber}}', {
          phoneNumber: formatPhoneNumber(phoneNumber),
        })}
      </Text>
    </div>
  );
};

export const PhoneNumberSetup: FC<PhoneNumberSetupProps> = ({ onStart }) => {
  const { t } = useTranslation('ai-receptionist');

  const phoneFieldProps = useFormField({
    type: 'phone',
    required: true,
    value: '',
    validateOnChange: true,
  });

  const clearHistoryFieldProps = useFormField({
    type: 'checkbox',
    value: true,
  });

  const handleStartDemo = () => {
    if (phoneFieldProps.value) {
      onStart(phoneFieldProps.value, clearHistoryFieldProps.value);
    }
  };

  const isButtonDisabled = !phoneFieldProps.value || !!phoneFieldProps.error;

  return (
    <div css={styles.container}>
      <Text css={styles.text}>
        {t('Please enter the phone number you would like the AI Receptionist to send appointment information to:')}
      </Text>

      <PhoneField {...phoneFieldProps} name='phoneNumber' label={t('Phone Number')} placeholder='(555) 555-5555' />

      <div css={styles.footer}>
        <CheckboxField
          {...clearHistoryFieldProps}
          name='clearMessageHistory'
          label={t('Clear previous conversation')}
        />

        <Button
          variant='primary'
          onClick={handleStartDemo}
          disabled={isButtonDisabled}
          trackingId={AIRSalesDemoTrackingIds.salesDemoStartDemoButton}
        >
          {t('Start Demo')}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  container: css`
    padding: var(--fabric-space-padding-md);
    border: 1px solid var(--fabric-color-border-subtle);
    border-radius: var(--fabric-radius-md);
    background: var(--fabric-color-surface-default);
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
    box-shadow: var(--fabric-elevation-light-200);
  `,
  text: css`
    color: var(--fabric-color-text-primary);
  `,
  footer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--fabric-space-gap-xxs);
  `,
  confirmationContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  icon: css`
    flex-shrink: 0;
  `,
};
